import * as React from "react";
import {
  Dispatch,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { useAppDropdownScroll } from "../hooks/useAppDropdownScroll";
import {
  getMenuNameFromPath,
  getSubMenuFromMainMenu,
} from "../components/common";
import {
  Alert,
  Col,
  Form,
  Nav,
  Navbar,
  NavDropdown,
  Row,
} from "react-bootstrap";
import MenuLinkButton from "../components/Button/MenuLinkButton";
import { Button, IconButton, Popover } from "@material-ui/core";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import type { SvgIconProps } from "@material-ui/core/SvgIcon";
import { Link, useHistory, useLocation } from "react-router-dom";
import { iSideBarNavigation } from "../types/props";
import {
  AccountCircleOutlined,
  EmailOutlined,
  FeedbackOutlined,
  FilterListOutlined,
  FormatListBulletedOutlined,
  HelpOutline,
  HowToRegOutlined,
  ImportantDevicesOutlined,
  InfoOutlined,
  MenuBookOutlined,
  NotificationsOutlined,
  PaymentOutlined,
  SearchOutlined,
  StorefrontOutlined,
  SyncOutlined,
  TuneOutlined,
  WbSunnyOutlined,
} from "@material-ui/icons";
import { ReactComponent as MegaphoneChangelogSvg } from "../assets/icons/megaphone.svg";
import UserAPIs from "../apis/user.apis";
import useIsMounted from "ismounted";

import {
  failed_action_response,
  iResource,
  iResponseActions,
  iStoreAction,
  loading_action_response,
  responseReducer,
  success_action_response,
} from "../redux/reducers";
import {
  AppDispatchContext,
  AppStateContext,
  ChatAppStateContext,
} from "../App";
import NotificationAPIs, { iNotification } from "../apis/notification.apis";
import WhatsNewAPIs from "../apis/whatsNew.apis";
import BaseAPIs from "../apis/base.apis";
import WhatsNewDrawer from "../components/WhatsNew/WhatsNewDrawer";
import FeedbackModal from "../components/WhatsNew/FeedbackModal";
import AppLoader from "../components/Loader/AppLoader";
import classNames from "classnames";
import CommonApis, { SearchResponse } from "../apis/common.apis";
import { Reducer } from "redux";
import RenderError from "../components/Error/Error";
import Axios, { CancelTokenSource } from "axios";
import { iEmailAutomation } from "../types/internal";
import { iEmailCampaign } from "../types/internal/email/campaign";
import { iEmailMailingList } from "../types/internal/email/mailinglist";
import { iEmailSegment } from "../apis/Email/email.segmentation";
import EmailAutomationAPIs from "../apis/Email/email.automations.apis";
import FormattedDate from "../components/Utils/FormattedDate";
import TimeFromNow from "../components/Utils/FromNow";
import { _HomeConfig } from "../components/Tour/Pages/Home.Tour";
import { useTour } from "@reactour/tour";
import SwipeableEWDrawer from "../components/Drawer";
import { useTranslation } from "react-i18next";
/** Header toolbar: icon + menu (same language list as Account → Profile display language). */
import LanguageControl from "../components/i18n/LanguageMenu";
import { shopPerm } from "../utils/shopPermissions";
import { APP_NAV_COMPACT_BREAKPOINT_PX } from "../constants/appLayout";

function notificationIconForType(
  type?: string,
): React.ComponentType<SvgIconProps> {
  const t = (type || "").toLowerCase();
  switch (t) {
    case "new_automations":
      return TuneOutlined;
    case "new_popups":
      return ImportantDevicesOutlined;
    default:
      break;
  }
  if (
    t.includes("registr") ||
    t.includes("signup") ||
    t === "account" ||
    t === "profile" ||
    t.includes("verify")
  ) {
    return HowToRegOutlined;
  }
  if (
    t.includes("welcome") ||
    t.includes("onboard") ||
    t.includes("getting_started")
  ) {
    return WbSunnyOutlined;
  }
  if (
    t.includes("email") ||
    t.includes("mail") ||
    t.includes("campaign") ||
    t.includes("newsletter")
  ) {
    return EmailOutlined;
  }
  if (
    t.includes("shop") ||
    t.includes("store") ||
    t.includes("commerce") ||
    t.includes("shopify")
  ) {
    return StorefrontOutlined;
  }
  if (t.includes("sync") || t.includes("import")) {
    return SyncOutlined;
  }
  if (
    t.includes("pay") ||
    t.includes("bill") ||
    t.includes("invoice") ||
    t.includes("subscription")
  ) {
    return PaymentOutlined;
  }
  if (t.includes("feedback") || t === "info" || t.includes("announce")) {
    return InfoOutlined;
  }
  return NotificationsOutlined;
}

export function ProfileOptions() {
  const { t } = useTranslation("header");

  const { loggedInUser, shop } = useContext(AppStateContext);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [avatarLoadFailed, setAvatarLoadFailed] = React.useState(false);
  const isMounted = useIsMounted();
  const history = useHistory();

  const dispatch: Dispatch<iStoreAction> = useContext(AppDispatchContext);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  function logout() {
    new UserAPIs().logout().then(() => {
      if (isMounted.current) {
        dispatch({ type: "logout" });
        history.replace("/login");
      }
    });
  }

  function myAccount() {
    history.replace("/account");
    setAnchorEl(null);
  }

  /** Prioritize the active shop customer; fallback to any owned customer with an image. */
  const currentCustomer = shop?.customer || loggedInUser?.customer;
  const customerWithAvatar =
    (currentCustomer && Boolean(currentCustomer.image) ? currentCustomer : null) ??
    loggedInUser?.customers?.find((c) => Boolean(c.image)) ??
    null;
  const avatarUrl = customerWithAvatar
    ? new UserAPIs().getCustomerAvatarURL(
        customerWithAvatar.uid,
        customerWithAvatar.updated_at,
      )
    : "";

  React.useEffect(() => {
    setAvatarLoadFailed(false);
  }, [avatarUrl]);

  const showAvatarPhoto = Boolean(avatarUrl) && !avatarLoadFailed;

  return (
    <>
      <IconButton
        aria-label={t("profile.aria.menu")}
        aria-controls="long-menu"
        aria-haspopup="true"
        onClick={handleClick}
      >
        {showAvatarPhoto ? (
          <img
            src={avatarUrl}
            alt=""
            className="profile-photo-header"
            onError={() => setAvatarLoadFailed(true)}
          />
        ) : (
          <AccountCircleOutlined
            className="app-main-navbar__profile-fallback-icon"
            aria-hidden
          />
        )}
      </IconButton>
      <Popover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        disableScrollLock
        onClose={handleClose}
        PaperProps={{ className: "ew-header-menu-paper", elevation: 0 }}
      >
        <nav
          className="ew-dropdown-panel ew-dropdown-panel--header-nav"
          aria-label={t("profile.aria.menu")}
        >
          <button
            type="button"
            className="ew-dropdown-option"
            onClick={myAccount}
          >
            {t("profile.labels.myAccount")}
          </button>
          <button type="button" className="ew-dropdown-option" onClick={logout}>
            {t("profile.labels.logout")}
          </button>
        </nav>
      </Popover>
    </>
  );
}

export function NotificationPopupOver() {
  const { t } = useTranslation("header");

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isMounted = useIsMounted();

  const history = useHistory();
  const { server_notifications } = useContext(AppStateContext);
  const dispatch: Dispatch<iStoreAction> = useContext(AppDispatchContext);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    dispatch({ type: "current_page_notification", page_number: 1 });
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  function NotificationItems({
    notification,
  }: {
    notification: iNotification;
  }) {
    const { t: tN } = useTranslation("header");

    const navigateFromNotification = () => {
      if (notification.type === "new_automations") {
        history.push("/email/automations", { open_import: true });
        return true;
      }
      if (notification.type === "new_popups") {
        history.push("/popups", { open_import: true });
        return true;
      }
      return false;
    };
    const TypeIcon =
      notificationIconForType(notification.type) ?? NotificationsOutlined;

    return (
      <div
        className={classNames(
          "notification-popup_content",
          notification.visibility === "1" ? "active" : "",
        )}
        onClick={() => {
          const navigated = navigateFromNotification();
          if (navigated) handleClose();
        }}
      >
        <div
          className="notification-popup_content_icon muted-icon-box"
          aria-hidden
        >
          <TypeIcon className="notification-popup_content_icon__svg muted-icon-box__svg" />
        </div>
        <div className="notification-popup_content_message">
          <div className="notification-popup_content_message__title-row">
            <span
              className="notification-popup_content_message__span"
              onClick={(e) => {
                e.stopPropagation();
                if (navigateFromNotification()) handleClose();
              }}
            >
              {notification.title}
            </span>
            {notification.message ? (
              <span className="notification-popup_content_message__message">
                {notification.message}
              </span>
            ) : null}
          </div>
          <div className="notification-popup_content_message__meta">
            {notification.created_at ? (
              <span
                className="notification-popup_content_message__date"
                onClick={(e) => {
                  e.stopPropagation();
                  if (navigateFromNotification()) handleClose();
                }}
              >
                <TimeFromNow date_string={notification.created_at} />
              </span>
            ) : null}
            <span
              className="notification-popup_content_message__mark"
              onClick={(e) => {
                e.stopPropagation();
                new NotificationAPIs()
                  .mark_as_read(notification.uid)
                  .then(() => loadResource());
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.stopPropagation();
                  new NotificationAPIs()
                    .mark_as_read(notification.uid)
                    .then(() => loadResource());
                }
              }}
              role="button"
              tabIndex={0}
            >
              {tN("notifications.actions.markAsRead")}
            </span>
          </div>
        </div>
      </div>
    );
  }

  const loadResource = useCallback(() => {
    dispatch({ type: "loading_notification" });
    new NotificationAPIs().list(server_notifications.query).then((response) => {
      if (isMounted.current) {
        if (NotificationAPIs.hasError(response)) {
          dispatch({ type: "error_notification", error: response.message });
        } else {
          dispatch({
            type: "response_notification",
            server_notification: response,
          });
        }
      }
    });
  }, [isMounted, server_notifications.query]);

  const notificationScrollBlocked =
    !server_notifications ||
    !!server_notifications.error_block ||
    !server_notifications.resource;

  const {
    scrollRef: notificationScrollRef,
    edge: notificationScrollEdge,
    pulseTop: notificationPulseTop,
    pulseBottom: notificationPulseBottom,
    syncScrollEdges: syncNotificationScroll,
  } = useAppDropdownScroll(
    Boolean(anchorEl),
    notificationScrollBlocked,
    server_notifications?.resource,
  );

  useEffect(() => {
    loadResource();
  }, [server_notifications.query]);

  const hasUnreadNotificationDot = useMemo(() => {
    const resource = server_notifications?.resource;
    if (!resource) return false;
    const unreadItems = (resource.items?.data ?? []).filter(
      (n: iNotification) => n.visibility !== "1",
    ).length;
    return (
      unreadItems > 0 ||
      !!resource.new_automations_count ||
      !!resource.new_popups_count
    );
  }, [server_notifications?.resource]);

  /** Match Header compact nav at APP_NAV_COMPACT_BREAKPOINT_PX: bell popover opens upward when anchored from the slim top bar. */
  const isMobileLayout = useMediaQuery(
    `(max-width:${APP_NAV_COMPACT_BREAKPOINT_PX}px)`,
  );
  const notificationAnchorOrigin = isMobileLayout
    ? ({ vertical: "top", horizontal: "right" } as const)
    : ({ vertical: "bottom", horizontal: "right" } as const);
  const notificationTransformOrigin = isMobileLayout
    ? ({ vertical: "bottom", horizontal: "right" } as const)
    : ({ vertical: "top", horizontal: "right" } as const);

  if (!server_notifications) return null;

  return (
    <>
      <IconButton
        aria-label={
          hasUnreadNotificationDot
            ? t("notifications.aria.menuUnread")
            : t("notifications.aria.menu")
        }
        aria-controls="Notifications-menu"
        aria-haspopup="true"
        onClick={handleClick}
        component="span"
      >
        <span className="notification-icon-with-badge">
          <NotificationsOutlined />
          {hasUnreadNotificationDot ? (
            <span className="notification-dot" aria-hidden />
          ) : null}
        </span>
      </IconButton>
      <Popover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        anchorOrigin={notificationAnchorOrigin}
        disableScrollLock
        transformOrigin={notificationTransformOrigin}
        transitionDuration={{ enter: 320, exit: 200 }}
        marginThreshold={8}
        className="ew-notification-popover__modal"
        style={{ zIndex: 10060 }}
        PaperProps={{
          className: classNames(
            "notification-popover-paper",
            isMobileLayout && "notification-popover-paper--anchor-up",
          ),
          elevation: 0,
        }}
        onClose={handleClose}
      >
        <div className="notification-popup">
          <div className="notification-popup__header">
            <NotificationsOutlined
              className="notification-popup__leading-icon"
              aria-hidden
            />
            <span className="notification-popup__title">
              {t("notifications.title")}
            </span>
          </div>
          <div className="notification-popup__content">
            {server_notifications.error_block && (
              <div className="notification-popup__error">
                <Alert>{server_notifications.error_block}</Alert>
              </div>
            )}
            {!server_notifications.error_block &&
              server_notifications.resource && (
                <div
                  className={classNames(
                    "app-dpanel-scroll__wrap",
                    notificationScrollEdge.overflow &&
                      "app-dpanel-scroll__wrap--overflow",
                    notificationScrollEdge.atTop &&
                      "app-dpanel-scroll__wrap--at-top",
                    notificationScrollEdge.atBottom &&
                      "app-dpanel-scroll__wrap--at-bottom",
                  )}
                >
                  {notificationScrollEdge.overflow && (
                    <>
                      <div
                        className={classNames(
                          "app-dpanel-scroll__edge",
                          "app-dpanel-scroll__edge--top",
                          notificationPulseTop &&
                            "app-dpanel-scroll__edge--pulse",
                        )}
                        aria-hidden
                      />
                      <div
                        className={classNames(
                          "app-dpanel-scroll__edge",
                          "app-dpanel-scroll__edge--bottom",
                          notificationPulseBottom &&
                            "app-dpanel-scroll__edge--pulse",
                        )}
                        aria-hidden
                      />
                    </>
                  )}
                  <div
                    ref={notificationScrollRef}
                    className="app-dpanel-scroll"
                    onScroll={syncNotificationScroll}
                  >
                    {!!server_notifications.resource.new_automations_count && (
                      <NotificationItems
                        notification={{
                          uid: "new_automations_count",
                          id: 12,
                          updated_at: "",
                          created_at: "",
                          title: t("notifications.counts.newAutomations", {
                            count:
                              server_notifications.resource
                                .new_automations_count,
                          }),
                          message: t("notifications.hints.automationsGallery"),
                          type: "new_automations",
                        }}
                      />
                    )}
                    {!!server_notifications.resource.new_popups_count && (
                      <NotificationItems
                        notification={{
                          uid: "new_popups_count",
                          id: 12,
                          updated_at: "",
                          created_at: "",
                          title: t("notifications.counts.newPopups", {
                            count:
                              server_notifications.resource.new_popups_count,
                          }),
                          message: t("notifications.hints.popupsGallery"),
                          type: "new_popups",
                        }}
                      />
                    )}
                    {server_notifications.resource.items &&
                      server_notifications.resource.items.data &&
                      server_notifications.resource.items.data.map(
                        (notification, index) => (
                          <NotificationItems
                            notification={notification}
                            key={index}
                          />
                        ),
                      )}
                    {server_notifications.loading && (
                      <div className="notification-popup__notifications__loading">
                        <AppLoader />
                      </div>
                    )}
                  </div>
                </div>
              )}
          </div>
        </div>
      </Popover>
    </>
  );
}

export interface iSearchGroup {
  title?: string;
  automations?: iEmailAutomation[];
  campaigns?: iEmailCampaign[];
  mail_lists?: iEmailMailingList[];
  segments?: iEmailSegment[];
}

export interface iSearchGroupContent {
  title?: string;
  body?: string;
  link?: string;
  icon: any;
}

export function TourMenu() {
  const { t } = useTranslation("header");
  const tourProps = useTour();
  const { setSteps, setIsOpen, setCurrentStep } = tourProps;
  const history = useHistory();

  const dispatch: Dispatch<iStoreAction> = useContext(AppDispatchContext);
  return (
    <span data-tut="reactour__skip_tour">
      <IconButton
        aria-label={t("tour.aria.start")}
        onClick={() => {
          setCurrentStep(0);
          setSteps(_HomeConfig(history, tourProps, dispatch));
          setIsOpen(true);
        }}
      >
        <MenuBookOutlined />
      </IconButton>
    </span>
  );
}

export function ProduktlyFeedback() {
  const { t } = useTranslation("header");
  const [open, setOpen] = React.useState(false);

  return (
    <span>
      <IconButton
        aria-label={t("feedback.aria.send")}
        onClick={() => setOpen(true)}
      >
        <FeedbackOutlined />
      </IconButton>
      <FeedbackModal open={open} onClose={() => setOpen(false)} />
    </span>
  );
}

export function ProduktlyNew() {
  const { t } = useTranslation("header");
  const [open, setOpen] = React.useState(false);
  const [unread, setUnread] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    new WhatsNewAPIs().unreadCount().then((res) => {
      if (
        !cancelled &&
        !BaseAPIs.hasError(res as any) &&
        typeof res.count === "number"
      ) {
        setUnread(res.count);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <span style={{ position: "relative", display: "inline-flex" }}>
      <IconButton
        aria-label={t("whatsNew.aria.open")}
        onClick={() => setOpen(true)}
      >
        <MegaphoneChangelogSvg
          className="app-main-navbar__megaphone-icon"
          aria-hidden
          focusable="false"
        />
      </IconButton>
      {unread > 0 && (
        <span
          style={{
            position: "absolute",
            top: 4,
            right: 4,
            minWidth: 16,
            height: 16,
            borderRadius: 8,
            background: "#e53935",
            color: "#fff",
            fontSize: 10,
            lineHeight: "16px",
            textAlign: "center",
            padding: "0 4px",
            pointerEvents: "none",
          }}
        >
          {unread > 9 ? "9+" : unread}
        </span>
      )}
      <WhatsNewDrawer
        open={open}
        onClose={() => setOpen(false)}
        onRead={() => setUnread(0)}
      />
    </span>
  );
}

export function SearchPopupOver() {
  const { t } = useTranslation("header");

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isMounted = useIsMounted();
  const history = useHistory();
  const [searchQuery, setSearchQuery] = useState<string>();
  const [{ response, error, loading, error_block }, dispatchResponse] =
    useReducer<
      Reducer<iResource<SearchResponse>, iResponseActions<SearchResponse>>
    >(responseReducer<iResource<SearchResponse>, any>({}), { loading: false });
  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const loadSearchResult = useCallback(
    (source: CancelTokenSource) => {
      dispatchResponse(loading_action_response());
      new CommonApis().search(searchQuery, source).then((res) => {
        if (isMounted.current) {
          if (CommonApis.hasError(res)) {
            dispatchResponse(failed_action_response(res.message));
          } else {
            dispatchResponse(success_action_response(res));
          }
        }
      });
    },
    [searchQuery],
  );

  useEffect(() => {
    const source = Axios.CancelToken.source();
    loadSearchResult(source);

    return () => {
      source.cancel();
    };
  }, [searchQuery]);

  const {
    scrollRef: searchResultsScrollRef,
    edge: searchScrollEdge,
    pulseTop: searchEdgePulseTop,
    pulseBottom: searchEdgePulseBottom,
    syncScrollEdges: syncSearchScrollEdges,
  } = useAppDropdownScroll(Boolean(anchorEl), loading || !!error, response);

  function SearchResultGroup(props: iSearchGroup) {
    return (
      <div className="search-popover__content__results__group">
        <div className="search-popover__content__results__heading">
          <span>{props.title}</span>
        </div>
        {props.campaigns &&
          props.campaigns.length > 0 &&
          props.campaigns.map((value, index) => {
            return <SearchResultCampaignContent key={index} {...value} />;
          })}
        {props.automations &&
          props.automations.length > 0 &&
          props.automations.map((value, index) => {
            return <SearchResultAutomationContent key={index} {...value} />;
          })}
        {props.mail_lists &&
          props.mail_lists.length > 0 &&
          props.mail_lists.map((value, index) => {
            return <SearchResultMailingListContent key={index} {...value} />;
          })}
        {props.segments &&
          props.segments.length > 0 &&
          props.segments.map((value, index) => {
            return <SearchResultSegmentContent key={index} {...value} />;
          })}
      </div>
    );
  }

  function SearchResultCampaignContent(props: iEmailCampaign) {
    return (
      <div className="search-popover__content__results__content">
        <div
          className="search-popover__content__results__content_icon"
          aria-hidden
        >
          <EmailOutlined className="search-popover__result-svg-icon" />
        </div>
        <div className="search-popover__content__results__content_result">
          <a
            href={`/email/campaigns/${props.uid}/overview`}
            className="search-popover__content__results__content_result__link"
          >
            {props.name}
          </a>
          <span className="search-popover__content__results__content_result__date">
            <FormattedDate
              date_string={props.created_at}
              format={"MMM D, YYYY"}
            />
          </span>
        </div>
      </div>
    );
  }

  function SearchResultAutomationContent(props: iEmailAutomation) {
    return (
      <div className="search-popover__content__results__content">
        <div
          className="search-popover__content__results__content_icon"
          aria-hidden
        >
          <TuneOutlined className="search-popover__result-svg-icon" />
        </div>
        <div className="search-popover__content__results__content_result">
          <a
            href={new EmailAutomationAPIs().getExternalEditorURL(props.uid)}
            className="search-popover__content__results__content_result__link"
          >
            {props.name}
          </a>
          <span className="search-popover__content__results__content_result__date">
            <FormattedDate
              date_string={props.created_at}
              format={"MMM D, YYYY"}
            />
          </span>
        </div>
      </div>
    );
  }

  function SearchResultMailingListContent(props: iEmailMailingList) {
    return (
      <div className="search-popover__content__results__content">
        <div
          className="search-popover__content__results__content_icon"
          aria-hidden
        >
          <FormatListBulletedOutlined className="search-popover__result-svg-icon" />
        </div>
        <div className="search-popover__content__results__content_result">
          <a
            href={`/email/lists/${props.uid}/view`}
            className="search-popover__content__results__content_result__link"
          >
            {props.name}
          </a>
          <span className="search-popover__content__results__content_result__date">
            <FormattedDate
              date_string={props.created_at}
              format={"MMM D, YYYY"}
            />
          </span>
        </div>
      </div>
    );
  }

  function SearchResultSegmentContent(props: iEmailSegment) {
    return (
      <div className="search-popover__content__results__content">
        <div
          className="search-popover__content__results__content_icon"
          aria-hidden
        >
          <FilterListOutlined className="search-popover__result-svg-icon" />
        </div>
        <div className="search-popover__content__results__content_result">
          <a
            href={`/email/segments/${props.id}`}
            className="search-popover__content__results__content_result__link"
          >
            {props.name}
          </a>
          <span className="search-popover__content__results__content_result__date">
            <FormattedDate
              date_string={props.created_at}
              format={"MMM D, YYYY"}
            />
          </span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className="d-inline-flex align-items-center app-main-navbar__search-trigger"
        aria-label={t("search.aria.trigger")}
        aria-controls="Search-menu"
        aria-haspopup="true"
        onClick={handleClick}
      >
        <SearchOutlined
          className="app-main-navbar__search-leading-icon"
          aria-hidden
        />
        <Form.Control
          type="text"
          value={searchQuery}
          style={{ border: 0, boxShadow: "none" }}
          placeholder={t("search.placeholder")}
          className="search-box header-search-box"
          onChange={(e) => {
            setSearchQuery(e.target.value);
            handleClick(e);
          }}
        />
      </div>
      <Popover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        disableScrollLock
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transitionDuration={{ enter: 320, exit: 200 }}
        PaperProps={{
          className: "search-popover__paper",
          elevation: 0,
        }}
        marginThreshold={8}
        onClose={handleClose}
      >
        <div className="search-popover">
          <div className="search-popover__input">
            <div className="search-popover__input-inner">
              <SearchOutlined
                className="search-popover__leading-icon"
                aria-hidden
              />
              <Form.Control
                type="text"
                style={{ border: 0, boxShadow: "none" }}
                value={searchQuery}
                placeholder={t("search.placeholder")}
                className="search-box header-search-box search-popover__search-input"
                autoFocus
                onChange={(e: any) => {
                  setSearchQuery(e.target.value);
                }}
              />
            </div>
          </div>
          <div className="search-popover__content__results">
            {RenderError({ error: error })}
            {loading && <AppLoader />}
            {!loading &&
              !error &&
              !(
                response &&
                response.campaigns &&
                response.campaigns.length > 0
              ) &&
              !(
                response &&
                response.automations &&
                response.automations.length > 0
              ) &&
              !(
                response &&
                response.mail_lists &&
                response.mail_lists.length > 0
              ) &&
              !(
                response &&
                response.segments &&
                response.segments.length > 0
              ) && (
                <div className="search-popover__content__results__no_results">
                  {t("search.empty.noResults")}
                </div>
              )}
            {!loading && !error && (
              <div
                className={classNames(
                  "app-dpanel-scroll__wrap",
                  searchScrollEdge.overflow &&
                    "app-dpanel-scroll__wrap--overflow",
                  searchScrollEdge.atTop && "app-dpanel-scroll__wrap--at-top",
                  searchScrollEdge.atBottom &&
                    "app-dpanel-scroll__wrap--at-bottom",
                )}
              >
                {searchScrollEdge.overflow && (
                  <>
                    <div
                      className={classNames(
                        "app-dpanel-scroll__edge",
                        "app-dpanel-scroll__edge--top",
                        searchEdgePulseTop && "app-dpanel-scroll__edge--pulse",
                      )}
                      aria-hidden
                    />
                    <div
                      className={classNames(
                        "app-dpanel-scroll__edge",
                        "app-dpanel-scroll__edge--bottom",
                        searchEdgePulseBottom &&
                          "app-dpanel-scroll__edge--pulse",
                      )}
                      aria-hidden
                    />
                  </>
                )}
                <div
                  ref={searchResultsScrollRef}
                  className="app-dpanel-scroll"
                  onScroll={syncSearchScrollEdges}
                >
                  {response &&
                    response.campaigns &&
                    response.campaigns.length > 0 && (
                      <SearchResultGroup
                        title={t("search.groups.campaigns")}
                        campaigns={response.campaigns}
                      />
                    )}
                  {response &&
                    response.automations &&
                    response.automations.length > 0 && (
                      <SearchResultGroup
                        title={t("search.groups.automations")}
                        automations={response.automations}
                      />
                    )}
                  {response &&
                    response.mail_lists &&
                    response.mail_lists.length > 0 && (
                      <SearchResultGroup
                        title={t("search.groups.mailLists")}
                        mail_lists={response.mail_lists}
                      />
                    )}
                  {response &&
                    response.segments &&
                    response.segments.length > 0 && (
                      <SearchResultGroup
                        title={t("search.groups.segments")}
                        segments={response.segments}
                      />
                    )}
                </div>
              </div>
            )}
          </div>
        </div>
      </Popover>
    </>
  );
}

export function HelpPopupOver() {
  const { t } = useTranslation("header");

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        aria-label={t("help.aria.menu")}
        aria-controls="Help-menu"
        aria-haspopup="true"
        onClick={handleClick}
        component="span"
      >
        <HelpOutline />
      </IconButton>
      <Popover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        disableScrollLock
        onClose={handleClose}
        PaperProps={{ className: "ew-header-menu-paper", elevation: 0 }}
      >
        <nav
          id="Help-menu"
          className="ew-dropdown-panel ew-dropdown-panel--header-nav"
          aria-label={t("help.aria.menu")}
        >
          <a
            href="https://www.youtube.com/channel/UCsZsVmPUNnvJblHDURUbWMQ/videos"
            target="_blank"
            rel="noopener noreferrer"
            className="ew-dropdown-option"
            onClick={handleClose}
          >
            {t("help.links.learnVideos")}
          </a>
          <a
            href="https://blog.emailwish.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="ew-dropdown-option"
            onClick={handleClose}
          >
            {t("help.links.blog")}
          </a>
          <a
            href="https://emailwish.com/terms-and-conditions/"
            target="_blank"
            rel="noopener noreferrer"
            className="ew-dropdown-option"
            onClick={handleClose}
          >
            {t("help.links.terms")}
          </a>
          <a
            href="https://emailwish.com/privacy-policy/"
            target="_blank"
            rel="noopener noreferrer"
            className="ew-dropdown-option"
            onClick={handleClose}
          >
            {t("help.links.privacy")}
          </a>
        </nav>
      </Popover>
    </>
  );
}

function Header() {
  const location = useLocation();
  const { t: tNav } = useTranslation("nav");
  const { loggedInUser } = useContext(AppStateContext);

  const { chat_stats } = useContext(ChatAppStateContext);
  const [menu_options, setMenuOptions] = useState<iSideBarNavigation[]>([]);

  useEffect(() => {
    const perms = loggedInUser?.shop_permissions;
    const menus = [
      {
        link: "/dashboard",
        text: tNav("main.home"),
        perm: "shop.dashboard.read",
        svg_node: undefined,
        "data-tut": "reactour__header_dashboard",
      },
      {
        link: "/email",
        text: tNav("main.email"),
        perm: "shop.email.read",
        svg_node: undefined,
        "data-tut": "reactour__header_email",
      },
      {
        link: "/reviews",
        text: tNav("main.reviews"),
        perm: "shop.reviews.read",
        svg_node: undefined,
        "data-tut": "reactour__header_reviews",
      },
      {
        link: "/chats",
        text: tNav("main.chats"),
        perm: "shop.chats_inbox.read",
        badgeCount: "",
        svg_node: undefined,
        "data-tut": "reactour__header_chats",
      },
      {
        link: "/popups",
        text: tNav("main.popups"),
        perm: "shop.popups.read",
        svg_node: undefined,
        "data-tut": "reactour__header_popups",
      },
      {
        link: "/wishlists",
        text: tNav("main.wishlists"),
        perm: "shop.wishlists.read",
        svg_node: undefined,
        "data-tut": "reactour__header_wishlists",
      },
    ];
    const filtered = perms
      ? menus
          .filter((m) => shopPerm(perms, m.perm))
          .map(({ perm, ...rest }) => rest)
      : menus.map(({ perm, ...rest }) => rest);
    setMenuOptions([...filtered]);
  }, [chat_stats, tNav, loggedInUser?.shop_permissions]);

  const [width, setWidth] = useState<number>(window.innerWidth);

  function handleWindowSizeChange() {
    setWidth(window.innerWidth);
  }

  useEffect(() => {
    window.addEventListener("resize", handleWindowSizeChange);
    return () => {
      window.removeEventListener("resize", handleWindowSizeChange);
    };
  }, []);

  const isCompactNav = width <= APP_NAV_COMPACT_BREAKPOINT_PX;

  const [toggleState, setToggleState] = useState(false);

  const opened_menu: string = getMenuNameFromPath(location.pathname, 1);

  return (
    <>
      {!isCompactNav ? (
        <Navbar expand="lg" className="app-main-navbar navbar-light sticky-top">
          <Navbar.Toggle
            onClick={() => setToggleState(true)}
            aria-controls="basic-navbar-nav"
          />
          <Navbar.Collapse
            id="basic-navbar-nav"
            className="app-main-navbar__collapse"
          >
            <Nav className="mr-auto app-main-navbar__links align-items-lg-center flex-nowrap">
              {!isCompactNav &&
                menu_options.map((menu, i) => {
                  let link = menu.link.replace("/", "");
                  let isActive = link === opened_menu;

                  if (menu.disabled && menu.comingSoon) {
                    return (
                      <span
                        key={`nav-soon-${i}`}
                        className="app-main-navbar__soon-wrap"
                      >
                        <span
                          className="app-main-navbar__coming-soon-pill"
                          aria-hidden
                        >
                          {tNav("main.comingSoon")}
                        </span>
                        <Button
                          component="span"
                          tabIndex={-1}
                          disableRipple
                          aria-disabled="true"
                          className="menu-button nav-button u500 app-main-navbar__link app-main-navbar__link--disabled-soon"
                        >
                          {menu.text}
                        </Button>
                      </span>
                    );
                  }

                  return (
                    <MenuLinkButton
                      link={menu.link}
                      key={
                        ((menu &&
                          menu.badgeCount &&
                          menu.badgeCount.toString()) ||
                          "-") + i
                      }
                      badgeCount={menu.badgeCount}
                      data-tut={menu["data-tut"]}
                      className={
                        "nav-button u500 app-main-navbar__link " +
                        (isActive ? "active" : "")
                      }
                    >
                      {menu.text}
                    </MenuLinkButton>
                  );
                })}

              {isCompactNav &&
                menu_options.map((menu, i) => {
                  let link = menu.link.replace("/", "");
                  return (
                    <NavDropdown
                      title={menu.text}
                      key={i}
                      id="basic-nav-dropdown"
                    >
                      {getSubMenuFromMainMenu(link, tNav).map((sub_menu, j) => {
                        return (
                          <NavDropdown.Item
                            href={sub_menu.link}
                            key={j}
                            data-tut={sub_menu["data-tut"]}
                          >
                            {sub_menu.text}
                          </NavDropdown.Item>
                        );
                      })}
                    </NavDropdown>
                  );
                })}
            </Nav>

            {!isCompactNav && (
              <div className="pull-right app-main-navbar__toolbar">
                <Row>
                  <Col className="d-flex justify-content-end align-items-center flex-wrap">
                    <div className="d-inline-flex align-items-center app-main-navbar__toolbar-inner">
                      <SearchPopupOver />
                      <TourMenu />
                      <NotificationPopupOver />
                      <HelpPopupOver />
                      <ProduktlyFeedback />
                      <ProduktlyNew />

                      <LanguageControl />
                      <ProfileOptions />
                    </div>
                  </Col>
                </Row>
              </div>
            )}
            {isCompactNav && (
              <Row>
                <Col className="d-flex justify-content-end align-items-center">
                  <div className="d-inline-flex align-items-center flex-wrap app-main-navbar__toolbar-inner">
                    <SearchPopupOver />
                    <TourMenu />
                    <NotificationPopupOver />
                    <HelpPopupOver />
                    <ProduktlyFeedback />
                    <ProduktlyNew />

                    <LanguageControl />
                    <ProfileOptions />
                  </div>
                </Col>
              </Row>
            )}
          </Navbar.Collapse>
        </Navbar>
      ) : (
        <SwipeableEWDrawer anchor="left" />
      )}
    </>
  );
}

export default Header;
